import NavigationModel, {NavigationLevelVO} from "./NavigationModel";
import {useLocation} from "react-router-dom";
import {RouteKey, FactoryID} from "../../model-factory/type-interface/ModelFactoryType";
import RouteFactory from "../../model-factory/basic/route/RouteFactory";
import RouteModel from "../../model-factory/basic/route/RouteModel";
import {IPopulateIDs, IRouteIDs} from "../../model-factory/type-interface/ModelFactoryInterface";
import RouteChoice from "../../model-factory/basic/route/RouteChoice";
import ModelFactoryModel from "../../model-factory/basic/ModelFactoryModel";
import {getModelFactory} from "../../model-factory/ModelFactory";
import {getManager} from "../Manager";
import {IStringObject} from "../../common/SimpleInterface";
import {cloneObject, cloneObjectTo, createFullHref, getObjectValues, idToSlug, slugToID} from "../../common/SiteHelper";
import {
    dispatchNavigationRouteChangeStart,
    dispatchNavigationRouteUpdate,
    dispatchRedirectTo
} from "../../event/DispatchEvent";
import DeferredPromise, {createDeferredPromise} from "../../common/DeferredPromise";
import ApiError from "../api/ApiError";

export default class NavigationManager
{
    initialized: boolean = false;
    haveInitialData: boolean = false;
    routeIsProcessing: boolean = false;
    waitingForAppLoad:boolean = false;
    navigationCount: number = 0;
    refreshCount: number = 0;
    findRefreshCount: number = 0;

    currentRoute: string [] = [];
    currentParams:IStringObject = {}
    currentHash:string = "";
    canNotProceedErrorMessage:string|null = null;
    lastLevelList:NavigationLevelVO[] = [];
    currentPathName:string = "/";
    currentURL:string = "/";
    model:NavigationModel = new NavigationModel();
    onRouteResolvedPromises:DeferredPromise<boolean, void>[] = [];
    currentLocation: {
        pathname: string
        hash: string
        search: string
    }|null = null;


    testPrint(value: any): void
    {
        if(this.haveInitialData){
            return
        }
        console.log(value);
    }

    async applyCurrentRoute(isRefresh:boolean = false): Promise<boolean>
    {
        let currentLocation: {
            pathname: string
            hash: string
            search: string
        }|null = null;

        if(!isRefresh){
            const location = useLocation();
            currentLocation = {
                pathname:location.pathname,
                hash:location.hash,
                search:location.search
            }
        } else if(this.currentLocation){
            this.findRefreshCount++;
            currentLocation = this.currentLocation;
        } else {
            return false;
        }

        let pathname = currentLocation.pathname;
        if(pathname.startsWith('/./')){
            pathname = pathname.substring(2);
        }


        const currentPathName = pathname;
        const currentURL = pathname + currentLocation.search + currentLocation.hash;

        const route: string [] = pathname.split("/").filter(Boolean);
        for(let i = 0; i < route.length; i ++){
            route[i] = slugToID(route[i]);
        }


        const locationSearch = currentLocation.search.length && currentLocation.search.charAt(0) === "?" ? currentLocation.search.substring(1) : currentLocation.search
        const searchStrings = locationSearch.split("&");
        const params:IStringObject = {};
        for(let i = 0; i < searchStrings.length; i ++){
            const keyValue = searchStrings[i].split("=");
            if(keyValue.length === 2){
                params[keyValue[0]] = keyValue[1];
            }
        }

        const hash = currentLocation.hash.length && currentLocation.hash.charAt(0) === "#" ? currentLocation.hash.substring(1) : currentLocation.hash


        let proceed =
            !this.initialized ||
            (route.length !== this.currentRoute.length) ||
            (Object.keys(params).length !== Object.keys(this.currentParams).length) ||
            (hash !== this.currentHash) ||
            (this.refreshCount !== this.findRefreshCount);

        if (!proceed)
        {
            for (let i = 0; i < route.length; i++)
            {
                if (route[i] !== this.currentRoute[i])
                {
                    proceed = true;
                    break;
                }
            }
        }

        if (!proceed)
        {
            for(let key in params)
            {
                if(this.currentParams[key] !== params[key])
                {
                    proceed = true;
                    break;
                }
            }
        }

        if (!proceed)
        {
            return false;
        }

        if(this.waitingForAppLoad){
            const promise = createDeferredPromise<boolean, void>();
            this.onRouteResolvedPromises.push(promise)
            return promise;
        }

        this.currentLocation = currentLocation;
        this.currentPathName = currentPathName;
        this.currentURL = currentURL;

        this.initialized = true;
        this.refreshCount = this.findRefreshCount;

        this.currentRoute.length = 0;
        this.currentRoute.push(...route);
        for (let key in this.currentParams)
        {
            delete this.currentParams[key];
        }
        for (let key in params)
        {
            this.currentParams[key] = params[key];
        }

        this.currentHash = hash;

        if(!this.routeIsProcessing){
            getManager().ui.primaryViewDidChange = false
        }

        dispatchNavigationRouteChangeStart();
        this.waitingForAppLoad = true;
        return getManager().app.requireAppLoadData().then((canNotProceedErrorMessage) => {
            this.waitingForAppLoad = false;
            this.canNotProceedErrorMessage = canNotProceedErrorMessage;
            if(this.canNotProceedErrorMessage){
                this.navigationCount ++;
                this.model = new NavigationModel();
                this.onDetermineRouteComplete();
                const valid = false;
                this.handleRouteResolved(valid)
                return valid;
            } else {
                return this.determineRoute().then((valid) =>{
                    this.handleRouteResolved(valid)
                    return valid
                });
            }
        })
    }

    async determineRoute(): Promise<boolean>
    {
        this.navigationCount ++;

        const currentNavigationCount = this.navigationCount;

        const onComplete = (didUpdate:boolean): Promise<boolean> => {
            return new Promise( async resolve =>
            {
                if(currentNavigationCount !== this.navigationCount){
                    return resolve(false);
                }

                if(didUpdate){
                    const valid = await this.determineRoute();
                    return resolve(valid);
                }

                const lastLevel = this.model.levelList[this.model.levelList.length - 1];
                if(lastLevel && !lastLevel.choiceID && lastLevel.model.goToFirstChild){
                    const firstChoice = lastLevel.model.firstVisibleChild();
                    if(firstChoice){
                        dispatchRedirectTo(firstChoice.href, false, true);
                        return resolve(true);
                    }
                }
                this.onDetermineRouteComplete();
                return resolve(true);
            });
        }

        const onDelay = () => {
            dispatchNavigationRouteUpdate();
        }

        const applyRoute = (navigationModel:NavigationModel): Promise<boolean> => {
            return new Promise( async resolve =>
            {
                this.model = navigationModel;
                this.routeIsProcessing = true;
                document.documentElement.classList.remove("nav-is-open");
                const didUpdate = await getManager().api.requirePopulateData(onDelay);
                const valid = await onComplete(didUpdate);
                return resolve(valid);
            });
        }

        const navigationModel = this.createNavigationModel(null, this.currentRoute, this.currentParams, this.currentHash);

        const routeIsLoading = !!getManager().api.getUnpopulatedDataModels(navigationModel.levelList).length;
        const primaryViewDidChange = !routeIsLoading ? (navigationModel.primaryHref !== this.model.primaryHref) : !this.model.primaryHref.startsWith(navigationModel.primaryHref);
        getManager().ui.primaryViewDidChange = getManager().ui.primaryViewDidChange || primaryViewDidChange;

        if(primaryViewDidChange && !getManager().ui.isImmediateRedirect){
            return getManager().ui.handlePageTransition().then(() =>{
                return applyRoute(navigationModel);
            });
        } else {
            return applyRoute(navigationModel);
        }
    }

    onDetermineRouteComplete = () => {
        this.lastLevelList = this.model.levelList.slice();

        this.testPrint("navigation_model");
        this.testPrint(this.model);

        getManager().ui.isImmediateRedirect = false;
        this.routeIsProcessing = false;
        this.haveInitialData = true;

        dispatchNavigationRouteUpdate();
    }

    createNavigationModel(choiceIDs?: IRouteIDs|null, choiceOrder?:string[]|null, params?:IStringObject|null, hash?:string|null): NavigationModel
    {
        const navigationModel = new NavigationModel();
        if(choiceOrder){
            choiceIDs = null;
        } else if(!choiceIDs){
            choiceOrder = choiceOrder ? choiceOrder : [];
        }
        let nextFactoryID:FactoryID | null = "browse_route";

        let arrayIndex:number = -1;
        const publicIdList:string[] = [];

        const knownParams:IStringObject = {};
        let knownHash:string = "";

        params = params ? params: {};
        hash = hash ? hash: "";

        while (nextFactoryID) {

            arrayIndex ++;

            const basicFactory = getModelFactory().getFactory(nextFactoryID);
            const factory = basicFactory instanceof RouteFactory ? basicFactory : null;
            const factoryID:FactoryID|null = factory?.id ?? null;
            if(!factory || !factoryID || navigationModel.models[factoryID]){
                break;
            }
            const useParams = getObjectValues(params, factory.getValidParamKeys(navigationModel.routeIDs, params));
            const useHash = factory.canUseHash(navigationModel.routeIDs) ? hash : "";

            const additionalRouteIDs = factory.getAdditionalRouteIDs(navigationModel.routeIDs, useParams, useHash, choiceIDs);
            cloneObjectTo(additionalRouteIDs, navigationModel.routeIDs, false, true);

            const populateIDs:IPopulateIDs = navigationModel.routeIDs;
            let model: RouteModel = factory.requireModel(populateIDs);

            let navigationRouteKey: RouteKey | null = model.navigationRouteKey;
            model = model as RouteModel;

            cloneObjectTo(useParams, knownParams);
            knownHash = knownHash ? knownHash : useHash;

            model.routeIDs = cloneObject(navigationModel.routeIDs);
            model.params = cloneObject(knownParams);
            model.hash = hash;
            model.realPath = navigationModel.realPath ? navigationModel.realPath : "/";

            this.applyRouteToModel(model, publicIdList);

            navigationModel.addRouteModel(model);

            const levelVO = new NavigationLevelVO(model, factoryID, navigationRouteKey);
            navigationModel.levelList.push(levelVO);


            levelVO.realPath = model.realPath;
            this.addDerivedModels(levelVO, model);

            let choice:RouteChoice | null = null;
            let skipRouteChoice = false;
            let previousRouteIDs: IRouteIDs = {};
            if(navigationRouteKey){
                navigationRouteKey = navigationRouteKey as RouteKey;
                let expectedChoiceID:string | null = null;

                if(choiceOrder){
                    expectedChoiceID = arrayIndex < choiceOrder.length ? choiceOrder[arrayIndex] : null;
                } else if(choiceIDs){
                    expectedChoiceID = choiceIDs[navigationRouteKey] ? choiceIDs[navigationRouteKey] as string : null;
                }

                expectedChoiceID = model.getValidChoiceID(expectedChoiceID);

                const predeterminedChoiceID = navigationModel.routeIDs[navigationRouteKey] ? navigationModel.routeIDs[navigationRouteKey] as string : null;

                let choiceID = expectedChoiceID;
                if(predeterminedChoiceID){
                    if(predeterminedChoiceID !== expectedChoiceID){
                        choiceID = predeterminedChoiceID;
                        skipRouteChoice = true;
                    }
                }

                choice = choiceID && model.choices[choiceID] ? model.choices[choiceID] : null;

                if(!choice){
                    if(choiceID && model.allowUnknownChoice){
                        choice = model.createUnknownChoice(choiceID);
                    } else if(!predeterminedChoiceID){
                        choiceID = model.getDefaultChoiceID();
                        choice = choiceID && model.choices[choiceID] ? model.choices[choiceID] : null;
                        skipRouteChoice = true;
                    } else {
                        skipRouteChoice = false;
                    }
                }
            }

            if(choice){
                navigationRouteKey = navigationRouteKey as RouteKey;
                const publicID:string = choice.publicID ? choice.publicID : choice.id;
                levelVO.choiceID = choice.id;
                levelVO.publicID = publicID;
                levelVO.choice = choice;
                navigationModel.choices[model.factoryID] = choice;
                navigationModel.routeIDs[navigationRouteKey] = choice.id;
                navigationModel.realPath += "/" + choice.id;
                const calculatedRouteIDs = choice.calculateRouteIDs ? choice.calculateRouteIDs() : null;
                if(calculatedRouteIDs){
                    cloneObjectTo(calculatedRouteIDs, navigationModel.routeIDs, false, true);
                }
                cloneObjectTo(choice.derivedRouteIDs, navigationModel.routeIDs, false, true);

                if(!skipRouteChoice){
                    publicIdList.push(levelVO.publicID);
                } else {
                    arrayIndex --;
                }
            } else {
                navigationModel.realPath = navigationModel.realPath ? navigationModel.realPath : "/";
            }
            for(let key in navigationModel.routeIDs){
                if(!previousRouteIDs[key]){
                    levelVO.routeIDs[key] = navigationModel.routeIDs[key];
                }
            }

            previousRouteIDs = cloneObject(navigationModel.routeIDs)


            nextFactoryID = choice ? choice.nextFactoryID : null;
        }

        const lastLevel  = navigationModel.levelList.length ? navigationModel.levelList[navigationModel.levelList.length - 1] : null;
        navigationModel.baseURL = lastLevel ? (lastLevel.choice ? lastLevel.choice.baseURL : lastLevel.model.baseURL) : "/";
        navigationModel.href = lastLevel ? (lastLevel.choice ? lastLevel.choice.href : lastLevel.model.href) : "/";
        navigationModel.primaryHref = "/";
        navigationModel.activeChoice = null;
        navigationModel.primaryChoice = null;
        navigationModel.knownParams = knownParams;

        for(let currentLevel of navigationModel.levelList){
            const choice = currentLevel.choice;

            if(!choice){
                continue
            }
            navigationModel.activeChoice = currentLevel.choice;
            if(getManager().view.isSecondaryChoiceRoute(choice) && navigationModel.primaryChoice){
                continue
            }
            const model = currentLevel.model;
            navigationModel.primaryChoice = currentLevel.choice;
            navigationModel.primaryHref = choice.href;
            navigationModel.primaryRouteIDs[model.navigationRouteKey!] = choice.id;
        }

        let matchesCurrentNavigation = true;
        for(let i = 0; i < navigationModel.levelList.length; i++){
            const levelVO = navigationModel.levelList[i];
            const model = levelVO.model;
            if(matchesCurrentNavigation && model !== this.lastLevelList[navigationModel.levelList.length - 1]?.model){
                matchesCurrentNavigation = false;
            }
            const modelList = ([model] as ModelFactoryModel[]).concat(levelVO.derivedModels);
            for(let i = 0; i < modelList.length; i++) {
                if(!matchesCurrentNavigation){
                    modelList[i].onWillAppear(navigationModel.levelList);
                }
            }
        }
        return navigationModel;
    }

    handleRouteResolved = (valid:boolean) => {
        if(valid){
            this.refreshCount = this.findRefreshCount;
        }
        for(let promise of this.onRouteResolvedPromises){
            promise.resolve(valid);
        }
        this.onRouteResolvedPromises.length = 0;
    }


    getChoiceNavigationModel(choice:RouteChoice):NavigationModel|null
    {
        if(!Object.keys(choice.routeIDs).length || !choice.nextFactoryID || (choice.nextFactoryID === "end_route")){
            return null;
        }
        return this.createNavigationModel(choice.routeIDs);
    }


    getChoiceNextModel(choice:RouteChoice)
    {
        const navigationModel = this.getChoiceNavigationModel(choice);
        if(!navigationModel){
            return null;
        }
        const model = navigationModel.levelList[navigationModel.levelList.length - 1]?.model ?? null;
        return model && (model.factoryID !== choice.factoryID) ? model : null;
    }

    getChoiceUiHref(choice:RouteChoice)
    {
        const model = this.getChoiceNextModel(choice);
        if(model){
            if(model && model.goToFirstChild && model.href.startsWith(choice.href)){
                const firstChild = model.firstVisibleChild();
                if(firstChild){
                    return firstChild.getHref();
                }
            }
        }
        return choice.href;
    }

    getSelectedChoice(fromChoiceList: RouteChoice[], allowDefault:boolean = false):RouteChoice|null
    {
        const realPath = this.model.realPath;
        let foundChoice:RouteChoice|null = null;
        for(let choice of fromChoiceList){
            if(realPath.startsWith(choice.realPath)){
                if(!foundChoice || choice.realPath.length > foundChoice.realPath.length){
                    foundChoice = choice;
                }
            }
        }
        return foundChoice ?? (allowDefault ? (fromChoiceList[0] ?? null) : null);
    }

    addDerivedModels(levelVO:NavigationLevelVO, model: ModelFactoryModel): void
    {
        const derivedModels = model.getDerivedModels();
        for(let i = 0; i < derivedModels.length; i++){
            const derivedModel = derivedModels[i];
            if(levelVO.derivedModels.includes(derivedModel)){
                continue;
            }
            levelVO.derivedModels.push(derivedModel);
            this.addDerivedModels(levelVO, derivedModel);
        }
    }

    applyRouteToModel(model:RouteModel, publicIdList:string[]): void
    {
        if(model.baseURL){
            return
        }

        let baseURL:string = "";
        for(let i = 0; i < publicIdList.length; i ++){
            baseURL += "/" + publicIdList[i];
        }
        baseURL = baseURL ? idToSlug(baseURL) : "/";
        model.baseURL = baseURL;
        model.href = createFullHref(model.baseURL, model.params, model.hash);
        model.applyRouteToChoices();
    }
}
